import gsap from 'gsap'
import DrawSVGPlugin from 'gsap/DrawSVGPlugin'

gsap.registerPlugin(DrawSVGPlugin)

export default class Itineraries {
	constructor() {
		this.sections = document.querySelectorAll('[data-itineraries-section]')
		this.#init()
	}

	#init() {
		this.sections.forEach(section => this.#setupSection(section))
	}

	#setupSection(section) {
		const buttons = section.querySelectorAll('[data-itineraries-button]')
		const contents = section.querySelectorAll('[data-itineraries-content]')
		const markerGroups = section.querySelectorAll(
			'[data-itineraries-markers]'
		)
		if (!buttons.length || !contents.length) return

		let activeIndex = 0
		let currentTween = null

		const getChildren = el => el.querySelectorAll(':scope > *')
		const getMarkers = group =>
			group?.querySelectorAll(
				':scope > *:not([data-itineraries-trail])'
			) ?? []

		contents.forEach((content, i) => {
			gsap.set(content, { autoAlpha: i === 0 ? 1 : 0 })
			gsap.set(getChildren(content), {
				autoAlpha: i === 0 ? 1 : 0,
				y: i === 0 ? 0 : 20,
			})
		})

		markerGroups.forEach((group, i) => {
			gsap.set(group, { autoAlpha: i === 0 ? 1 : 0 })
			gsap.set(getMarkers(group), {
				autoAlpha: i === 0 ? 1 : 0,
				scale: i === 0 ? 1 : 0.6,
			})
		})

		// The visible polyline stays statically dashed; we animate the mask's
		// polyline via DrawSVG so the dashes are revealed in place along the path.
		const getTrail = group =>
			group?.querySelector('[data-itineraries-trail-mask-line]')

		const drawTrail = (line, delay = 0) => {
			if (!line) return
			gsap.killTweensOf(line)
			gsap.fromTo(
				line,
				{ drawSVG: '0 0' },
				{
					drawSVG: '0 100%',
					duration: 1.2,
					ease: 'power2.out',
					delay,
				}
			)
		}

		const hideTrail = line => {
			if (!line) return
			gsap.killTweensOf(line)
			gsap.set(line, { drawSVG: '0 0' })
		}

		// Initial state: hide all trails, draw the first one in.
		markerGroups.forEach((group, i) => {
			const line = getTrail(group)
			if (i === 0) drawTrail(line, 0.2)
			else hideTrail(line)
		})

		const setActive = index => {
			if (index === activeIndex) return

			buttons.forEach((btn, i) =>
				btn.classList.toggle('active', i === index)
			)

			const outgoingContent = contents[activeIndex]
			const incomingContent = contents[index]
			const outgoingMarkers = markerGroups[activeIndex]
			const incomingMarkers = markerGroups[index]
			activeIndex = index

			currentTween?.kill()

			const tl = gsap.timeline()
			tl.to(getChildren(outgoingContent), {
				autoAlpha: 0,
				y: -20,
				duration: 0.35,
				ease: 'power2.in',
				stagger: 0.05,
			})
				.set(outgoingContent, { autoAlpha: 0 })
				.set(incomingContent, { autoAlpha: 1 })
				.fromTo(
					getChildren(incomingContent),
					{ autoAlpha: 0, y: 20 },
					{
						autoAlpha: 1,
						y: 0,
						duration: 0.5,
						ease: 'power2.out',
						stagger: 0.08,
					}
				)

			if (outgoingMarkers) {
				tl.to(
					getMarkers(outgoingMarkers),
					{
						autoAlpha: 0,
						scale: 0.6,
						duration: 0.3,
						ease: 'power2.in',
						stagger: 0.03,
					},
					0
				).set(outgoingMarkers, { autoAlpha: 0 }, '>')
			}

			if (incomingMarkers) {
				tl.set(incomingMarkers, { autoAlpha: 1 }, 0.3).fromTo(
					getMarkers(incomingMarkers),
					{ autoAlpha: 0, scale: 0.6 },
					{
						autoAlpha: 1,
						scale: 1,
						duration: 0.4,
						ease: 'back.out(2)',
						stagger: 0.06,
					},
					0.3
				)
			}

			hideTrail(getTrail(outgoingMarkers))
			tl.call(() => drawTrail(getTrail(incomingMarkers)), null, 0.3)

			currentTween = tl
		}

		buttons.forEach((button, i) => {
			button.addEventListener('click', () => setActive(i))
		})
	}
}
